import {
  TProduct,
  TProductAttribute,
  TProductBuyBox,
  TProductCashbackInfo,
  TProductFlags,
  TProductImage,
  TProductReviews,
  TProductValidation,
  TProductVariant,
  TProductVariants,
} from 'types/product';
import { CHANNEL_TYPE } from 'types/product/channel.types';
import { PRODUCT_DEFAULT_IMAGE } from './constants';

// Essa função bem como seu uso deve ser removidos após o fix do produto no Product Hub
function fixImageProtocol(url: string) {
  if (url) {
    return url.replace('hhttp', 'http');
  }
  return url;
}

function handleImages(hit: Record<string, any>): TProductImage[] {
  if (!hit.imagens || hit.imagens.length === 0) {
    return [
      {
        thumb: PRODUCT_DEFAULT_IMAGE,
        desc: PRODUCT_DEFAULT_IMAGE,
        zoom: PRODUCT_DEFAULT_IMAGE,
      },
    ];
  }

  return hit.imagens.map((image: string) => {
    const fixedImage = fixImageProtocol(image);

    return {
      thumb: fixedImage,
      desc: fixedImage,
      zoom: fixedImage,
    };
  });
}

const parseHitFlags = ({ flags }) => {
  const data = {};
  if (!flags) {
    return data;
  }
  Object.keys(flags).forEach(flag => {
    data[flag] = {
      name: flag,
      value: flags[flag],
      order: 0,
      group: '',
    };
  });
  return data;
};

function getSelectedMiniBuyBox({ buyBox = [] }: Record<string, any>, sellerId = null) {
  return (
    buyBox.find(
      ({ seller: { id: buyBoxSeller } }) => sellerId === null || buyBoxSeller === Number(sellerId)
    ) || {}
  );
}

function getCashbackValue(selectedMiniBuyBox: Record<string, any>): number {
  const { cashbackInfo = null } = selectedMiniBuyBox;

  if (!cashbackInfo) {
    return 0;
  }

  const { total } = cashbackInfo;

  return total;
}

function getBuyBoxFlags(selectedMiniBuyBox: Record<string, any>): Record<string, any> {
  const { flags = {} } = selectedMiniBuyBox;

  return flags;
}

export function handleFlags(
  hit: Record<string, any>,
  sellerId?: string,
  product?: Record<string, any>
): TProductFlags {
  let flags: TProductFlags = !Array.isArray(hit.flags) ? hit.flags : {};
  const flagsToExclude: Array<string> = ['blackfriday'];

  const selectedMiniBuyBox = product ? getSelectedMiniBuyBox(product, sellerId) : {};

  const cashbackValue = getCashbackValue(selectedMiniBuyBox);

  const cashbackFlag = {
    id: '',
    group: '',
    name: 'is_in_stock',
    order: 0,
    value: !hit.out_of_stock,
  };

  const cashbackFlag3p = {
    id: '',
    group: '',
    name: 'buybox.cashbackInfo.total',
    order: 0,
    value: cashbackValue,
  };

  if (hit.extra && hit.extra.merchandising) {
    flags = parseHitFlags({ flags: hit.extra.merchandising });
  }

  if (hit.flags && hit.flags[0]) {
    Object.keys(hit.flags[0]).forEach(flag => {
      if (!flagsToExclude.includes(flag)) {
        flags[flag] = {
          id: '',
          name: flag,
          value: hit.flags[0][flag],
          order: 0,
          group: '',
        };
      }
    });
  }

  flags.is_in_stock = cashbackFlag;
  flags['buybox.cashbackInfo.total'] = cashbackFlag3p;

  const buyBoxFlags = getBuyBoxFlags(selectedMiniBuyBox);

  Object.keys(flags).forEach(key => {
    if (key.includes('is_fg') || key.includes('is_free')) {
      delete flags[key];
    }
  });

  Object.entries(buyBoxFlags).forEach(([key, value]) => {
    flags[`buybox.flags.${key}`] = value;
  });

  return flags;
}

function handleReviews(hit: Record<string, any>): TProductReviews {
  return {
    isTopRecommended: hit.extra?.reviews?.is_top_recommended || false,
    rate: hit.extra?.reviews?.rate || 0,
    rateRounded: hit.extra?.reviews?.rate_rounded || 0,
    totalOpinions: hit.extra?.reviews?.total_opinions || 0,
    totalRecommendations: hit.extra?.reviews?.total_recommends || 0,
  };
}

function handleCashbackInfo(
  miniBuyBox: Record<string, any>,
  cashbackChannel: string
): TProductCashbackInfo {
  const { channels = [] } = miniBuyBox?.cashback_info || {};

  const cashbackInfo = channels.find(({ channel }) => channel === cashbackChannel) || null;

  if (!cashbackInfo) {
    return null;
  }

  return {
    channel: cashbackInfo.channel,
    channelId: cashbackInfo.channel_id,
    type: cashbackInfo.tipo,
    total: cashbackInfo.total,
    promotionId: 0,
    percentSeller: 0,
    percentMM: 0,
  };
}

function handleBuyBox(hit: Record<string, any>): TProductBuyBox[] {
  if (!hit.mini_buy_box || hit.mini_buy_box.length === 0) {
    return null;
  }

  return hit.mini_buy_box.map((miniBuyBox: any): TProductBuyBox => {
    const cashbackInfo = handleCashbackInfo(miniBuyBox, CHANNEL_TYPE.SITE);
    const installmentToDisplay = {
      number: miniBuyBox?.ultima_parcela?.parcela || 10,
      value: miniBuyBox?.ultima_parcela?.valor || 0,
      total: miniBuyBox?.ultima_parcela?.total || miniBuyBox.preco_12,
      fractionalValue: miniBuyBox?.ultima_parcela?.valorFracionado || 0,
      fractionalTotal: miniBuyBox?.ultima_parcela?.totalFracionado || 0,
    };

    const lastInstallmentWithNoInterest = {
      number:
        miniBuyBox?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.parcela ||
        installmentToDisplay.number,
      value:
        miniBuyBox?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.valor ||
        installmentToDisplay.value,
      total:
        miniBuyBox?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.total ||
        installmentToDisplay.total,
      fractionalValue:
        miniBuyBox?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.valorFracionado ||
        installmentToDisplay.fractionalValue,
      fractionalTotal:
        miniBuyBox?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.totalFracionado ||
        installmentToDisplay.fractionalTotal,
    };

    const lastInstallmentWithInterest = miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros
      ?.parcela
      ? {
          number:
            miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.parcela ||
            installmentToDisplay.number,
          value:
            miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.valor ||
            installmentToDisplay.value,
          total:
            miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.total ||
            installmentToDisplay.total,
          fractionalValue:
            miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.valorFracionado ||
            0,
          fractionalTotal:
            miniBuyBox?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.totalFracionado ||
            0,
        }
      : null;

    return {
      stock: miniBuyBox?.stock?.total || 0,
      outOfStock: miniBuyBox?.out_of_stock || false,
      seller: {
        id: miniBuyBox?.id_seller || 0,
        slug: miniBuyBox?.seller_slug || '',
      },
      price: {
        productMDiscount: miniBuyBox?.percentual_desconto_sku || 0,
        incashDiscount: miniBuyBox?.percentual_avista || 0,
        productMPrice: miniBuyBox?.preco_de || 0,
        inCash: miniBuyBox?.preco_promocional || 0,
        total: miniBuyBox?.preco_12 || 0,
        fractionalPrice: miniBuyBox?.preco_fracionado || 0,
      },
      installmentToDisplay,
      lastInstallmentWithNoInterest,
      lastInstallmentWithInterest,
      cashbackInfo,
      flags: parseHitFlags(miniBuyBox),
      unit: {
        name: hit?.unidade,
        value: hit?.unidade_fracionada,
      },
    };
  });
}

function handleVariantItem(variant: Record<string, any>): TProductVariant {
  const installmentToDisplay = {
    number: variant?.ultima_parcela?.parcela || 10,
    value: variant?.ultima_parcela?.valor || 0,
    total: variant?.ultima_parcela?.total || variant.preco_12,
  };

  const lastInstallmentWithNoInterest = {
    number:
      variant?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.parcela ??
      installmentToDisplay.number,
    value:
      variant?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.valor ??
      installmentToDisplay.value,
    total:
      variant?.parcelamento_detalhado?.ultima_parcela_semjuros?.parcela?.total ??
      installmentToDisplay.total,
  };

  const lastInstallmentWithInterest = variant?.parcelamento_detalhado?.ultima_parcela_comjuros
    ?.parcela
    ? {
        number:
          variant?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.parcela ??
          installmentToDisplay.number,
        value:
          variant?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.valor ??
          installmentToDisplay.value,
        total:
          variant?.parcelamento_detalhado?.ultima_parcela_comjuros?.parcela?.total ??
          installmentToDisplay.total,
      }
    : null;

  return {
    id: variant.id_produto,
    name: variant.nome,
    url: variant.url,
    images: [
      {
        thumb: variant.thumb,
        desc: variant.zoom,
        zoom: variant.zoom,
      },
    ],
    buyBox: {
      stock: variant?.estoque || 0,
      outOfStock: false,
      price: {
        productMDiscount: variant?.percentual_desconto_sku || 0,
        incashDiscount: variant?.percentual_avista || 0,
        productMPrice: variant?.preco_de || 0,
        inCash: variant?.preco_promocional || 0,
        total: variant?.preco_12 || 0,
      },
      installmentToDisplay,
      lastInstallmentWithNoInterest,
      lastInstallmentWithInterest,
    },
  };
}

function handleProductVariant(prevVal: any, product: string) {
  const { adaptedVariants, hitVariants, option, type } = prevVal;
  if (adaptedVariants.items[product]) {
    adaptedVariants.items[product].slugs.push(
      hitVariants[option][type][product]?.slug_atributo || null
    );
  } else {
    adaptedVariants.items[product] = {
      ...hitVariants[option][type][product],
      slugs: [hitVariants[option][type][product]?.slug_atributo || null],
    };
  }

  adaptedVariants.options[option] = {
    ...adaptedVariants.options[option],
    [hitVariants[option][type][product].slug_atributo]: {
      name: type,
      alias: hitVariants[option][type][product]?.slug_atributo || null,
    },
  };

  return {
    adaptedVariants,
    hitVariants,
    option,
    type,
  };
}

function handleVariants(hit: Record<string, any>): TProductVariants {
  let adaptedVariants: TProductVariants = {
    options: {},
    items: {},
    toShow: [],
  };

  if (!hit.variantes) {
    return adaptedVariants;
  }

  const hitVariants = hit.variantes;

  Object.keys(hitVariants).forEach(option => {
    Object.keys(hitVariants[option]).forEach(type => {
      if (type) {
        ({ adaptedVariants } = Object.keys(hitVariants[option][type]).reduce(handleProductVariant, {
          adaptedVariants,
          hitVariants,
          option,
          type,
        }));
      }
    });
  });

  let toShow = Object.values(adaptedVariants.items);

  if (Object.values(adaptedVariants.options).length > 1) {
    const currentSlugs = adaptedVariants.items[hit.id_produto].slugs;
    toShow = toShow.filter(
      (item: any) =>
        item.slugs === currentSlugs ||
        (!item.slugs.includes(currentSlugs[0]) && item.slugs.includes(currentSlugs[1]))
    );
  }

  adaptedVariants.toShow = toShow
    .map((variant: Record<string, any>) => handleVariantItem(variant))
    .filter(variant => variant.buyBox.stock > 0);

  if (adaptedVariants.toShow.length > 0) {
    const selectedVariantIndex = adaptedVariants.toShow.findIndex(
      (item: any) => item.id === hit.id_produto
    );
    adaptedVariants.toShow.unshift(adaptedVariants.toShow.splice(selectedVariantIndex, 1)[0]);
  }

  return adaptedVariants;
}

function handleAttributes(hit: Record<string, any>): TProductAttribute[] {
  return hit?.product_hub?.attributes || [];
}

export function isValidHit(hit: Omit<TProduct, 'validation' | 'flags'>): TProductValidation {
  const hasId = !!hit.id;
  const hasName = !!hit.name;
  const hasUrl = !!hit.url;
  const hasBuyBox = !!hit.buyBox && Array.isArray(hit.buyBox) && hit.buyBox.length > 0;
  const hasImages = !!hit.images && Array.isArray(hit.images) && hit.images.length > 0;

  const isValid = hasId && hasName && hasUrl && hasImages && hasBuyBox;

  return {
    isValid,
    hasId,
    hasName,
    hasUrl,
    hasBuyBox,
    hasImages,
  };
}

export function handleMiniBuyBoxFlags({ buyBox }: Record<string, any>): Record<string, any> {
  return buyBox.reduce((prevVal: Record<string, any>, item: any) => {
    const sellerId = item.seller.id === 1 ? 0 : item.seller.id;
    return {
      ...prevVal,
      [sellerId]: item.flags,
    };
  }, {});
}

export default function productHitAdapter(hit: Record<string, any>, sellerId?: string): TProduct {
  if (!hit) {
    return null;
  }

  const product: Omit<TProduct, 'validation' | 'flags'> = {
    objectID: hit.objectID,
    id: hit.id_produto,
    ean: hit?.ean || '',
    name: hit.nome,
    url: hit?.url || null,
    brand: hit?.marca || '',
    color: hit?.atributos?.Cor || '',
    images: handleImages(hit),
    reviews: handleReviews(hit),
    buyBox: handleBuyBox(hit),
    variants: handleVariants(hit),
    attributes: handleAttributes(hit),
    variantIds: hit?.product_hub?.variants || [],
    category_tree: hit?.slug?.[0]?.url_listagem || null,
    category_id: hit?.id_categoria || null,
    category_name: hit?.slug?.[0]?.departamento_pai || null,
    new_category: hit?.product_hub?.commercial_category ?? 'Sem Categoria',
    // eslint-disable-next-line no-underscore-dangle
    queryID: hit?.__queryID || null,
    // eslint-disable-next-line no-underscore-dangle
    position: hit?.__position || null,
    seller_id: hit?.mini_buy_box?.[0]?.id_seller ?? null,
    seller_name: hit?.nome_seller || null,
    resolvedBidId: hit?.resolvedBidId || null,
    sponsoredData: hit?.sponsoredData || null,
    reviewHighlights: hit?.review_highlights || null,
  };

  return {
    ...product,
    validation: isValidHit(product),
    flags: handleFlags(hit, sellerId, product),
    miniBuyBoxFlags: handleMiniBuyBoxFlags(product),
  };
}
